<template>
    <div>
        <span class="text-lg">
            Remooc đầy đủ pháp lý
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.legallySufficient')"
                :value="$formatters.formatCurrency(dataAnalytics.totalLegallySufficient)"
                color="success"
                increase
                @click="getItems('legallySufficient')" />
            <analytics-card
                :text="$t('labels.approachingExpiration')"
                :value="$formatters.formatCurrency(dataAnalytics.totalApproachingExpiration)"
                color="accent"
                increase
                @click="getItems('approachingExpiration')" />
            <analytics-card
                :text="$t('labels.notlegallySufficient')"
                :value="$formatters.formatCurrency(dataAnalytics.totalNotlegallySufficient)"
                color="error"
                increase
                @click="getItems('notLegallySufficient')" />
        </div>
    </div>
    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import { CoveringContainerLevel, GoodsTypes } from '../../../../vehicle/ignore/imports/form'
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'
import { $api, $lodash } from '#imports'

const dataAnalytics = ref<{
  totalLegallySufficient: number
  totalApproachingExpiration: number
  totalNotlegallySufficient: number
}>({
  totalLegallySufficient: 0,
  totalApproachingExpiration: 0,
  totalNotlegallySufficient: 0
})

onMounted(async () => {
  const api = $url.dashboard.fleetVehicleManagement.getNearingExpirationRemmoc
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')
  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const items = ref<Array<any>>([])
const store = $store()
const { $formatters } = useNuxtApp()

const loading = ref(false)
const visible = ref(false)

const getItems = async (type: string) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.fleetVehicleManagement.getDataNearingExpirationRemmoc
    const data: any = await $api(api, { uriParams: { TYPE: type } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const listLabelCont: { [key: string]: any } = {
  10: $t('labels.vehicleModule.10ft'),
  20: $t('labels.vehicleModule.20ft'),
  40: $t('labels.vehicleModule.40ft'),
  45: $t('labels.vehicleModule.45ft'),
  50: $t('labels.vehicleModule.other')
}

const headers = [
  {
    field: 'licensePlate',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.remoocModule.licensePlate'),
    sortable: true
  },
  {
    field: 'manufacture',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.remoocModule.manufacture')
  },
  {
    field: 'load',
    filter: 'agNumberColumnFilter',
    headerName: $t('labels.vehicleLoadByKg'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.load)
    }
  },
  {
    field: 'coveringContainerLevel',
    label: 'coveringContainerLevel',
    headerName: $t('labels.remoocModule.remoocCategory.coveringContainerLevel'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (params.data.coveringContainerLevel === CoveringContainerLevel.PRIVATE) {
        return $t('labels.remoocModule.remoocCategory.private')
      } else if (params.data.coveringContainerLevel === CoveringContainerLevel.OPEN) {
        return $t('labels.remoocModule.remoocCategory.open')
      }
    }
  },
  {
    field: 'goodsTypes',
    label: 'goodsTypes',
    headerName: $t('labels.remoocModule.remoocCategory.goodsTypes'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      const goodsType = params.data.goodsType
      if (goodsType.length > 0) {
        const formatGoodsType = goodsType.map((item: any) => {
          if (item === '') {
            return item
          }
          if (item === GoodsTypes.SOLID) {
            item = $t('labels.remoocModule.remoocCategory.solid')
          } else if (item === GoodsTypes.LIQUID) {
            item = $t('labels.remoocModule.remoocCategory.liquid')
          } else {
            item = $t('labels.remoocModule.remoocCategory.gas')
          }
          return item
        })
        return formatGoodsType.toString()
      }
    }
  },
  {
    field: 'isTowingContainer',
    headerName: $t('labels.remoocModule.remoocCategory.towingCapacity'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      const { isTowingContainer } = params.data
      return isTowingContainer ? $t('labels.remoocModule.remoocCategory.isTowing') : $t('labels.remoocModule.remoocCategory.isNotTowing')
    }
  },
  {
    field: 'containerType',
    headerName: $t('labels.suitableContainerType'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params.data.towingCapacity || !params.data.towingCapacity.length || !params.data.isTowingContainer) {
        return null
      }
      const result: { [key: string]: any } = []
      params.data.towingCapacity.forEach((i: string) => {
        if (listLabelCont[i]) {
          result.push(listLabelCont[i])
        }
      })
      return result.join(',')
    }
  },
  {
    field: 'price',
    filter: 'agNumberColumnFilter',
    headerName: $t('labels.remoocModule.price'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.price)
    }
  },
  {
    field: 'depreciation',
    filter: 'agNumberColumnFilter',
    headerName: $t('labels.depreciationPeriodRemoocByYear'),
    sortable: true
  },
  {
    field: 'sellingPrice',
    filter: 'agNumberColumnFilter',
    headerName: $t('labels.remoocModule.sellingPrice'),
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.sellingPrice)
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (params.data.status === 1) {
        return $t('labels.remoocModule.active')
      }
      if (params.data.status === -1) {
        return $t('labels.remoocModule.inactive')
      }
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'dashboardDataTable',
      storeName: 'Dashboard'
    }
  }
]
</script>
